import React, { useEffect, useState } from 'react'

// components
import { Box, Typography } from '@mui/material'
import AdxActionButton from 'components/reusable/ActionButton/AdxActionButton'
import AdxButton from 'components/custom/AdxButton'

// services
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getAllShifts } from 'services/api/time-attendance'
import { formatTimeToAmPm } from 'services/helper/DateTime'

// icons
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'

const AttendanceSettings = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: shiftData } = useQuery(['getAllShifts'], async () => {
    const { data } = await getAllShifts()
    return data
  })

  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)

  useEffect(() => {
    if (location.state?.snackbarStatus) {
      setSuccessSnackbarOpen(true)
    }
  }, [location.state])

  const renderSnackbarMessage = () => {
    if (location.state?.snackbarStatus) {
      switch (location.state?.snackbarStatus) {
        case 1:
          return 'Shift created successfully'
        case 2:
          return 'Shift updated successfully'
        case 3:
          return 'Shift deleted successfully'
        case 4:
          return 'Draft created successfully'
        // case 5:
        //   return 'Shift has been deactivated successfully'
        default:
          return ''
      }
    }
  }

  const [isCustomHours, setIsCustomHours] = useState(false)

  useEffect(() => {
    if (shiftData?.shifts) {
      const customHoursFlags = shiftData.shifts.map((element) => {
        let initialValue = null
        let initialValue2 = null

        const allSame = element.working_time.every((time, index) => {
          if (index === 0) {
            if (time.total_hours) {
              initialValue = time.total_hours
            } else {
              initialValue = time.start_time
              initialValue2 = time.end_time
            }
            return true
          } else {
            if (time.total_hours) {
              return time.total_hours === initialValue
            } else {
              return time.start_time === initialValue && time.end_time === initialValue2
            }
          }
        })

        return !allSame
      })

      setIsCustomHours(customHoursFlags)
    }
  }, [shiftData?.shifts])

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'dark-text.main' }}>
            Shift schedule
          </Typography>
          <Typography variant="body1" sx={{ color: 'light-gray-text.main' }}>
            Create and manage your company’s shift schedule and keep track your employee’s
            attendance
          </Typography>
        </Box>
        {shiftData?.shifts?.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '20px',
              backgroundColor: 'white.main',
              padding: '48px 24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'default-background.main',
                borderRadius: '100%',
                border: 1,
                borderColor: 'gray-line.main',
                width: '90px',
                height: '90px',
              }}
            >
              <TimerOutlinedIcon sx={{ color: 'light-gray-text.main', fontSize: '36px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Typography sx={{ color: 'light-gray-text.main' }}>
                No shifts have been scheduled yet
              </Typography>
            </Box>
            <AdxButton
              variant={'outlined'}
              sx={{ width: '130px', gap: '8px' }}
              onClick={() => {
                navigate('/app/management-centre/company-management/time-attendance/shift/create')
              }}
            >
              <AddIcon />
              <Typography noWrap>Add shift</Typography>
            </AdxButton>
          </Box>
        ) : (
          <Box sx={{ border: 1, borderColor: 'gray-line.main', borderRadius: '4.5px' }}>
            {shiftData?.shifts?.map((item, index) => (
              <Box
                key={index}
                onClick={() =>
                  navigate(
                    `/app/management-centre/company-management/time-attendance/shift/${item.id}`
                  )
                }
                sx={{
                  backgroundColor: 'white.main',
                  padding: '20px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: 1,
                  borderBottomColor: 'gray-line.main',
                  cursor: 'pointer',
                  borderRadius: index === 0 ? '4.5px 4.5px 0 0' : 0,
                  ':hover': {
                    backgroundColor: 'default-background.main',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography variant="subtitle1">{item.name}</Typography>
                  {item.status !== 'draft' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Typography
                        sx={{
                          color:
                            item.status === 'active'
                              ? 'positive.main'
                              : item.status === 'scheduled'
                              ? 'information.main'
                              : 'light-gray-text.main',
                          textTransform: 'capitalize',
                        }}
                      >
                        {item.status} <span style={{ color: '#8A8A8A' }}> • </span>
                      </Typography>
                      <Typography sx={{ color: 'light-gray-text.main' }}>
                        {item.staff_count} {item.staff_count === 1 ? 'employee' : 'employees'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {item.status === 'draft' ? (
                    <Box
                      sx={{
                        padding: '12px',
                        borderRadius: '4.5px',
                        backgroundColor: 'light-warning.main',
                        color: 'warning.main',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      Draft
                    </Box>
                  ) : (
                    <Typography>{`${
                      isCustomHours[index]
                        ? 'Custom hours'
                        : item.working_time[0]?.total_hours
                        ? item.working_time[0]?.total_hours + ' hours'
                        : item.working_time[0]?.start_time && item.working_time[0]?.end_time
                        ? formatTimeToAmPm(item.working_time[0]?.start_time, true) +
                          ' - ' +
                          formatTimeToAmPm(item.working_time[0]?.end_time, true)
                        : 'No working hours set'
                    } (${item.working_day_count} days)`}</Typography>
                  )}
                  <ChevronRightIcon />
                </Box>
              </Box>
            ))}
            <AdxActionButton
              title="Add new shift"
              icon="add"
              iconColor="alternate.main"
              iconBgColor="white.main"
              onClick={() => {
                navigate('/app/management-centre/company-management/time-attendance/shift/create')
              }}
              divClick
              sx={{ color: 'alternate.main', borderRadius: '0 0 4.5px 4.5px' }}
            />
          </Box>
        )}
      </Box>
      <SnackbarRecruitment
        autoHideDuration={2600}
        status="success"
        TransitionComponent="slide"
        message={renderSnackbarMessage()}
        open={successSnackbarOpen}
        handleClose={() => setSuccessSnackbarOpen(false)}
      />
    </>
  )
}

export default AttendanceSettings
